import { FC, ReactNode, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "gatsby"
import classnames from "classnames"
import {
  Button,
  Container,
  Stack,
  Typography,
  UserIcon
} from "@eventbrite/marmalade"
import Lottie from "react-lottie"

// utils
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"
import { returnUrlWithLangCodePrefixOnly } from "../../utils/general_utils"
import { goToLoginPage } from "../ProfileMenuBar/ProfileMenuBar"
import { getCantLogInPath } from "./LogIn"

// types
import { RootState } from "../../store/store"

// styles
import * as styles from "./login.module.scss"

// lottie data
import * as greenLoaderLottieData from "../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

type Props = {
  loginHeaderMicrocopy: Queries.HelpArticleQuery["loginHeaderMicrocopy"]
  loginContentMicrocopy: Queries.HelpArticleQuery["loginContentMicrocopy"]
  loginButtonMicrocopy: Queries.HelpArticleQuery["loginButtonMicrocopy"]
  cantLogInMicrocopy: Queries.HelpArticleQuery["cantLogInMicrocopy"]
  cantLogInArticle?: Queries.HelpArticleQuery["cantLogInArticle"]
  mobileSizeOnly?: boolean
  containerClassName?: string
}

export const LogInLegacy: FC<Props> = ({
  loginButtonMicrocopy,
  loginContentMicrocopy,
  loginHeaderMicrocopy,
  cantLogInMicrocopy,
  cantLogInArticle,
  mobileSizeOnly,
  containerClassName
}) => {
  const cantLogInPath = getCantLogInPath(cantLogInArticle)
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state?.metaData?.hasAttemptedLogin
  )
  const isLoggedIn = useSelector((state: RootState) => state?.userProfile)
    ? true
    : false
  const isLoggingIn = !hasAttemptedLogin && !isLoggedIn

  const loginHeaderMicrocopyRendered = useMemo<ReactNode>(() => {
    return loginHeaderMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginHeaderMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [loginHeaderMicrocopy])
  const loginContentMicrocopyRendered = useMemo<ReactNode>(() => {
    return loginContentMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginContentMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [loginContentMicrocopy])
  const loginButtonMicropyRendered = useMemo<ReactNode>(() => {
    return loginButtonMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          loginButtonMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : ""
  }, [])

  const cantLogInMicrocopyRendered = useMemo<ReactNode>(() => {
    return cantLogInMicrocopy?.microcopyValue?.raw
      ? renderRichText(
          cantLogInMicrocopy.microcopyValue.raw,
          customRendererToRemovePTag
        )
      : "Can't log in?"
  }, [cantLogInMicrocopy])

  const [cantLogInHref, setCantLogInHref] = useState(cantLogInPath)
  useEffect(() => {
    setCantLogInHref(
      returnUrlWithLangCodePrefixOnly(cantLogInPath) ?? cantLogInPath
    )
  }, [cantLogInPath])

  if (isLoggedIn && hasAttemptedLogin) {
    return null
  }

  return (
    <Container
      className={classnames(
        styles.logInContainer,
        mobileSizeOnly ? styles.logInMobileSizeOnlyContainer : "",
        containerClassName
      )}
    >
      <div className={styles.outerWrapper}>
        <div className={styles.iconWrapper}>
          <UserIcon size="xlarge" className={styles?.iconWrapper} />
        </div>

        <Stack className={styles.innerWrapper}>
          <Stack>
            <Typography
              variant="heading-sm"
              as="h3"
              className={styles.headingText}
            >
              {loginHeaderMicrocopyRendered}
            </Typography>

            <Typography variant="body-md" className={styles.loginContentText}>
              {loginContentMicrocopyRendered}{" "}
              <Link
                to={cantLogInHref}
                className={styles.cantLoginLinkInline}
                data-heap-id="link.login-component.cant-log-in"
              >
                {cantLogInMicrocopyRendered}
              </Link>
            </Typography>
          </Stack>

          <Stack className={styles.buttonWrapper} space="spacing-md">
            <Button
              size="block"
              variant="primary"
              className={styles.btn}
              onClick={!isLoggingIn ? () => goToLoginPage() : () => null}
              data-heap-id="button.login-component.log-in"
            >
              {isLoggingIn ? (
                <Lottie
                  options={{
                    loop: true,
                    autoplay: true,
                    animationData: greenLoaderLottieData,
                    rendererSettings: {
                      preserveAspectRatio: "xMidYMid slice"
                    }
                  }}
                  width={24}
                  height={24}
                />
              ) : (
                loginButtonMicropyRendered
              )}
            </Button>
          </Stack>
        </Stack>
      </div>
    </Container>
  )
}
